
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  shallowRef,
  ref,
  toRef,
  watch,
} from "vue";
import { IBulkDelete } from "@/interfaces/iPageModel";
import { ITabConfig } from "@/interfaces/iTabConfig";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import { TranslationService } from "@/core/services/TranslationService";
import { INNER_PAGE_ROWS_PER_PAGE, usePagination } from "@/helpers/pagination";
import { FormatMethods } from "@/helpers/data-format";
import * as Yup from "yup";
import VZDeleteModal from "@/components/content/VZDeleteModal.vue";
import {
  siteLocationsTableBulkDelete,
  siteLocationTableFilters,
  locationSiteTableHeaders,
} from "../siteDataTableOptions";
import { ISite } from "@/interfaces/iSite";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import { useToast } from "vue-toastification";
import { FORM_TABS } from "@/views/form/dataTableOptions";
import PreviewLink from "@/components/typography/PreviewLink.vue";
import { hasPermission } from "@/core/services/permission.service";
import { LocationAPI } from "@/services/api/Location.service";
import { ISiteLocation } from "@/interfaces/iLocation";
import SiteDetailModal from "@/views/site/modal/SiteDetailModal.vue";
import { debounce } from "lodash";
export default defineComponent({
  props: {
    data: { type: Object as PropType<ISite>, required: true },
    openDrawer: { type: Boolean, default: false },
    isActive: { type: Boolean, default: false },
  },
  components: { Datatable, VZDeleteModal, PreviewLink, SiteDetailModal },
  setup(props) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    let options: { label: any; value: string }[] = [];
    const dataProp = toRef(props, "data");
    const openDrawer = toRef(props, "openDrawer");
    const { page, total } = usePagination();
    const rowsPerPage = ref(INNER_PAGE_ROWS_PER_PAGE);
    const loading = ref(false);
    const showSearch = true;
    const tableData = ref<ISiteLocation[]>([]);
    const allTableData = ref<ISiteLocation[]>([]);
    const showDeleteDialog = ref<any>(false);
    const selectedForAction = ref<Partial<ISiteLocation>>({});
    const localPreference = ref({});
    const bulkDelete = ref<IBulkDelete>(siteLocationsTableBulkDelete());
    const tableHeader = ref(locationSiteTableHeaders());
    const tableFilter = ref(siteLocationTableFilters());
    const showDetailDialog = ref<any>(false);
    const loadingSiteDetailModal = ref(false);
    const format = computed(() => {
      return FormatMethods;
    });
    const Datatable = ref();
    const tabs = shallowRef<ITabConfig[]>(FORM_TABS());
    const viewName = "siteLocations";
    const toast = useToast();
    const message = t("common.deleteSuccessMessage");
    const formAddedMsg = t("pages.user.locationAssignedMsg");
    let formData = ref<IAddEditFormModel>({
      page: "Site Location",
      header: "Site Location",
      edit: false,
      data: null,
      validationSchema: Yup.object({
        locationId: Yup.array()
          .nullable()
          .min(1, t("pages.location.location") + " " + t("common.isRequired"))
          .required(
            t("pages.location.location") + " " + t("common.isRequired")
          ),
      }),
      label: [{ addHeader: t("pages.location.assignLocation") }],
      model: [
        {
          name: "locationId",
          label: t("pages.location.location"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select location",
          options: options,
          multiSelect: true,
        },
      ],
    });
    let permissionModel = ref<any>({
      create: "VYZOR:BUSINESS:LOCATIONS:CREATE",
      delete: "VYZOR:BUSINESS:LOCATIONS:DELETE",
      update: "VYZOR:BUSINESS:LOCATIONS:UPDATE",
    });
    onMounted(async () => {
      try {
        // queryUpdate(dataProp.value.webId, {
        //   page: page.value - 1,
        //   size: rowsPerPage.value,
        // });
        loading.value = true;
      } catch (err) {
        console.log("error in getting location list: ", err);
      }
    });
    const getLocations = async () => {
      options = [] as any;
      const res =
        (await LocationAPI.getInstance().getLocationListOptions()) as any;
      options = [];
      res.map((data) => {
        options.push({
          label: data.text,
          value: data.value,
        });
      });
      const locationIds = allTableData.value.map((item) =>
        Number(item.locationId)
      );
      const filteredValues = options.filter(
        (item) => !locationIds.includes(Number(item.value))
      );
      formData.value.model[0].options = filteredValues;
    };
    const edit = (item: any) => {
      formData.value.data = { ...item };
      formData.value.edit = true;
    };

    const submit = async (reqData) => {
      loading.value = true;
      await LocationAPI.getInstance()
        .addMultiplesLocation({
          siteId: dataProp.value.webId,
          locationIds: reqData.locationId,
        })
        .then(() => {
          toast.success(formAddedMsg);
          queryUpdate(dataProp.value.webId, { ...localPreference.value });
        })
        .catch((err) => {
          console.log("error in assigning form: ", err);
        })
        .finally(() => {
          loading.value = false;
        });
    };
    const syncQuery = async (params?: Record<string, number | string>) => {
      queryUpdate(dataProp.value.webId, params);
    };

    const queryUpdate = async (
      webId: number,
      params?: Record<string, number | string>
    ) => {
      try {
        loading.value = true;
        if (!params || !params.sort) {
          params = {
            ...params,
            sort: "createdDate,desc",
          };
        }
        const data = await LocationAPI.getInstance().siteLocations({
          ...params,
          searchLocation: params.search,
          siteId: dataProp.value.webId,
        });
        const allData = await LocationAPI.getInstance().siteLocations({
          status: "ACTIVE",
          size: 10000,
          siteId: dataProp.value.webId,
        });
        localPreference.value = { ...params };
        tableData.value = [...data.content];
        allTableData.value = [...allData.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
        getLocations();
      } finally {
        loading.value = false;
      }
    };
    const onBulkDelete = async (ids) => {
      showDeleteDialog.value = false;
      let deletedMsg = "Selected location(s) have been deleted successfully.";
      await LocationAPI.getInstance()
        .deleteSiteLocation(ids)
        .then(() => {
          toast.success(deletedMsg);
          refreshList();
        })
        .catch(() => {
          //
        })
        .finally(() => {
          loading.value = false;
        });
    };
    const refreshList = () => {
      showDeleteDialog.value = false;
      queryUpdate(dataProp.value.webId, { ...localPreference.value });
    };

    const openDetailModal = async (form) => {
      try {
        loadingSiteDetailModal.value = true;
        const data = await LocationAPI.getInstance().getSiteById(form.siteId);
        // selectedUser.value = data;
      } finally {
        loadingSiteDetailModal.value = false;
      }
    };

    const webId = computed(() => props.data?.webId);
    let lastFetchedWebId = null;

    const debouncedQueryUpdate = debounce(async (webId) => {
      if (webId !== lastFetchedWebId) {
        lastFetchedWebId = webId;
        tableData.value = [];
        allTableData.value = [];

        rowsPerPage.value = INNER_PAGE_ROWS_PER_PAGE;
        queryUpdate(+webId, {
          page: page.value - 1,
          size: rowsPerPage.value,
        });
      }
    }, 300);

    watch(
      () => props.isActive,
      async (isActive) => {
        if (isActive && dataProp.value.webId) {
          // await queryUpdate(dataProp.value.webId, {
          //   page: page.value - 1,
          //   size: rowsPerPage.value,
          // });
          loading.value = true;
          if (Datatable.value?.VZFilter) {
            await Datatable.value.VZFilter.getAppliedFilters();
          }
        } else {
          tableData.value = [];
          allTableData.value = [];
        }
      }
    );
    return {
      Datatable,
      bulkDelete,
      format,
      tableData,
      tableHeader,
      tableFilter,
      showSearch,
      page,
      tabs,
      total,
      rowsPerPage,
      loading,
      selectedForAction,
      syncQuery,
      formData,
      submit,
      refreshList,
      viewName,
      edit,
      onBulkDelete,
      openDetailModal,
      permissionModel,
      hasPermission,
      loadingSiteDetailModal,
      showDeleteDialog,
      showDetailDialog,
      t,
      getLocations,
    };
  },
});
